/**
 * Server state for step 1 — every read and write goes through a contract route (packages/contracts).
 * Lists a screen needs whole (to count, group or filter locally) follow `next_cursor` to the end.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ChangeSpocBody,
  ClientRow,
  CreateClientBody,
  CreateUserBody,
  GrantableCapability,
  UpdateClientBody,
  UpdateUserBody,
  User,
} from '@amplispot/contracts';
import { ApiFailure, call, callAll, newIdempotencyKey } from '../api';

export const keys = {
  clients: ['clients'] as const,
  client: (id: string) => ['client', id] as const,
  spocs: (id: string) => ['client', id, 'spocs'] as const,
  users: ['users'] as const,
};

/** GET /clients, every page, priority order (high → low), active clients only (the contract default). */
export function useClients(enabled = true) {
  return useQuery({
    queryKey: keys.clients,
    queryFn: async (): Promise<ClientRow[]> => (await callAll('clients.list', { sort: 'priority' })) as ClientRow[],
    enabled,
  });
}

/** GET /users, every page — active and disabled. Managers only (`users.read`); pass `enabled` from `can()`. */
export function useUsers(enabled = true) {
  return useQuery({
    queryKey: keys.users,
    queryFn: async (): Promise<User[]> => (await callAll('users.list', {})) as User[],
    enabled,
  });
}

export function useClient(id: string | null) {
  return useQuery({
    queryKey: keys.client(id ?? ''),
    queryFn: () => call('clients.get', { params: { id: id! } }),
    enabled: !!id,
  });
}

export function useSpocHistory(id: string | null) {
  return useQuery({
    queryKey: keys.spocs(id ?? ''),
    queryFn: () => call('spocs.history', { params: { id: id! } }),
    enabled: !!id,
  });
}

export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { body: CreateClientBody; key: string }) => call('clients.create', { body: v.body, idempotencyKey: v.key }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.clients });
      void qc.invalidateQueries({ queryKey: keys.users });
    },
  });
}

export function useUpdateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; version: number; body: UpdateClientBody }) =>
      call('clients.update', { params: { id: v.id }, body: v.body, ifMatch: v.version }),
    // Returned (not `void`ed) so the mutation settles only after the rows carry the new version: the next inline
    // edit on the same row sends that version as If-Match, and must not race the refetch into a false 412.
    onSuccess: (_d, v) => Promise.all([qc.invalidateQueries({ queryKey: keys.clients }), qc.invalidateQueries({ queryKey: keys.client(v.id) })]),
  });
}

export function useChangeSpoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { clientId: string; body: ChangeSpocBody; key: string }) =>
      call('spocs.change', { params: { id: v.clientId }, body: v.body, idempotencyKey: v.key }),
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: keys.clients });
      void qc.invalidateQueries({ queryKey: ['client', v.clientId] });
      void qc.invalidateQueries({ queryKey: keys.users });
    },
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { body: CreateUserBody; key: string }) => call('users.create', { body: v.body, idempotencyKey: v.key }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.users }),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; version: number; body: UpdateUserBody }) =>
      call('users.update', { params: { id: v.id }, body: v.body, ifMatch: v.version }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.users });
      void qc.invalidateQueries({ queryKey: keys.clients });
      // Changing your own role or name changes your menu and top bar.
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
    // 412: someone else saved this person first. Reload the list so the row (and its version) shows
    // their change; otherwise every retry would send the same stale version and fail again.
    onError: (e) => {
      if (e instanceof ApiFailure && e.status === 412) void qc.invalidateQueries({ queryKey: keys.users });
    },
  });
}

/**
 * Grant (on) or revoke (off) a capability held on top of the person's role (operator decisions 11–12).
 * PUT/DELETE /users/{id}/capabilities/{capability}: both idempotent, and neither changes the person's version.
 */
export function useSetCapability() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; capability: GrantableCapability; on: boolean }) =>
      call(v.on ? 'users.grantCapability' : 'users.revokeCapability', { params: { id: v.id, capability: v.capability } }),
    onSettled: () => void qc.invalidateQueries({ queryKey: keys.users }),
  });
}

export { newIdempotencyKey };
